import type { TFunction } from "i18next";
import { HRIS_SOURCES } from "../../pages/Settings/DepartmentConfiguration/forms/generalMetadataConsts";
import type { RoleColorResolver } from "../../utils/categoricalColors";
import { EffectiveEmployeeStatus, ExternalSource, type Job, type NurseFullRole } from "../../utils/models";
import { inverseRolesMap, nurseRoleDisplayName } from "../../utils/utilFunctions";
import type { FacetFilterOption } from "../UIToolkit/Filters/FacetFilterSection";
import { TEAM_SECTIONS } from "./config";
import {
	LAST_LOGIN_8_30_ID,
	LAST_LOGIN_31_90_ID,
	LAST_LOGIN_BUCKET_IDS,
	LAST_LOGIN_NEVER_ID,
	LAST_LOGIN_OVER_90_ID,
	LAST_LOGIN_PAST_7_ID,
	NO_AGENCY_FILTER_ID,
	NO_JOB_FILTER_ID,
	NO_UNION_FILTER_ID,
	type TeamFacetCounts,
	UNNAMED_AGENCY_FILTER_ID,
} from "./teamFacetFilters";

/**
 * Pure option-builders + helpers for the Team faceted filter sidebar. Kept out of
 * `TeamFilterSidebar.tsx` (which stays wiring/render) so the sidebar respects the
 * ~200-line budget and the option logic stays independently readable/testable.
 * Each builder takes already-computed counts + the current selection and returns the
 * `FacetFilterOption[]` a `FacetFilterSection` renders.
 */

/** View-toggle option id (reveals rows) that lives inside the Status group but routes to `showOnlyDecisions`. */
export const NEEDS_DECISION_OPTION_ID = "needs-decision";

// Reuses the EmployeeStatusChip display names so the sidebar and the Status column agree.
const STATUS_LABEL_KEYS: Record<EffectiveEmployeeStatus, string> = {
	[EffectiveEmployeeStatus.Active]: "employee.status.chip.active",
	[EffectiveEmployeeStatus.Inactive]: "employee.status.chip.inactive",
	[EffectiveEmployeeStatus.OnLeave]: "employee.status.chip.on_leave",
	[EffectiveEmployeeStatus.Suspended]: "employee.status.chip.suspended",
};

const INTEGRATOR_LABELS: ReadonlyMap<string, string> = new Map(HRIS_SOURCES.map(({ value, label }) => [value, label]));

/**
 * Brand-name fallback for sources missing from HRIS_SOURCES (e.g. clipboard): "matrix_hr" → "Matrix Hr".
 * Takes `t` because the demo source maps to a translated phrase, not a brand name.
 */
export function integratorLabel(source: string, t: TFunction, isDemoDepartment = false): string {
	// A demo department stands in for a real HRIS feed, so show that instead of "Demo". Gated on the
	// department, not just the source: "demo" is selectable in Settings, and a real department that
	// picked it should still read "Demo".
	if (isDemoDepartment && source === ExternalSource.Demo) return t("team.filter.integrator.generic_hris");
	const known = INTEGRATOR_LABELS.get(source);
	if (known) return known;
	return source
		.split("_")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");
}

/**
 * Two-tier option visibility: options that don't exist in the facility at all
 * (baseline 0) are dropped entirely; options that exist but are filtered to zero
 * stay visible as disabled. Checked options always render.
 */
export function visibleByBaseline(options: FacetFilterOption[], baselineOf: (id: string) => number): FacetFilterOption[] {
	return options.filter((option) => option.checked || baselineOf(option.id) > 0);
}

/**
 * Shared builder for the groups whose options come from an async source list (Roles, Jobs). It maps
 * the source ids to options AND merges any currently-checked id the source list lacks — a stale or
 * renamed value that would otherwise match no rendered option and strand the roster at zero rows with
 * no checkbox to clear it. Sentinel ids (e.g. NO_JOB) are valid-but-not-in-source; the caller lists
 * them (pass `[]` when none) so they're excluded from the stale set and appends them itself. Generic
 * over the id type `T` so callers get a typed `toOption(id)` with no `as` cast; per-group
 * labeling/counts live in `toOption`.
 */
function buildSourceListOptions<T extends string>(params: {
	sourceIds: readonly T[];
	selectedIds: readonly T[];
	sentinelIds: readonly T[];
	toOption: (id: T) => FacetFilterOption;
	baselineOf: (id: string) => number;
}): FacetFilterOption[] {
	const { sourceIds, selectedIds, sentinelIds, toOption, baselineOf } = params;
	const inSource = new Set<T>(sourceIds);
	const staleChecked = selectedIds.filter((id) => !inSource.has(id) && !sentinelIds.includes(id));
	return visibleByBaseline([...sourceIds, ...staleChecked].map(toOption), baselineOf);
}

/**
 * Shared builder for the groups whose options come from the roster BASELINE keys (Union, Agency,
 * Integrator): merge any checked value the baseline lacks (stale-safe, like the source-list groups),
 * sort by label, then append the group's "None" sentinel. `sentinel.alwaysShow` keeps it visible even
 * at baseline 0 (Agency reveals rows, so its "None" is always there); `disabled` is applied to every
 * option (Agency stays clickable at count 0). Per-group labeling lives in `toLabel`.
 */
function buildBaselineKeyOptions(params: {
	baselineKeys: Iterable<string>;
	selected: ReadonlySet<string>;
	counts: ReadonlyMap<string, number>;
	toLabel: (key: string) => string;
	disabled?: boolean;
	sentinel: { id: string; label: string; count: number; baseline: number; alwaysShow?: boolean };
}): FacetFilterOption[] {
	const { baselineKeys, selected, counts, toLabel, disabled, sentinel } = params;
	const keys = new Set<string>([...baselineKeys, ...[...selected].filter((id) => id !== sentinel.id)]);
	const options: FacetFilterOption[] = [...keys]
		.map((key) => ({ id: key, label: toLabel(key), count: counts.get(key) ?? 0, checked: selected.has(key), disabled }))
		.sort((a, b) => a.label.localeCompare(b.label));
	if (sentinel.alwaysShow || sentinel.baseline > 0 || selected.has(sentinel.id)) {
		options.push({ id: sentinel.id, label: sentinel.label, count: sentinel.count, checked: selected.has(sentinel.id), disabled });
	}
	return options;
}

// ── Status group: real per-department statuses (OR'ed) + the needs-decision toggle ──
export function buildStatusOptions(params: {
	t: TFunction;
	facetCounts: TeamFacetCounts;
	selectedStatuses: ReadonlySet<string>;
	showOnlyDecisions: boolean;
	decisionsCount: number;
}): FacetFilterOption[] {
	const { t, facetCounts, selectedStatuses, showOnlyDecisions, decisionsCount } = params;
	const options = visibleByBaseline(
		Object.values(EffectiveEmployeeStatus).map((status) => ({
			id: status,
			label: t(STATUS_LABEL_KEYS[status]),
			count: facetCounts.statuses.get(status) ?? 0,
			checked: selectedStatuses.has(status),
		})),
		(id) => facetCounts.baselines.statuses.get(id) ?? 0,
	);
	if (decisionsCount > 0) {
		options.push({
			id: NEEDS_DECISION_OPTION_ID,
			label: t("team.filter.needs_decision"),
			count: facetCounts.needsDecision,
			checked: showOnlyDecisions,
			// A view toggle that REVEALS rows, not a narrowing facet: exempt from the
			// zero-count auto-disable so it stays interactive at count 0.
			disabled: false,
		});
	}
	return options;
}

// ── Roles ──
export function buildRoleOptions(params: {
	facetCounts: TeamFacetCounts;
	departmentNurseTypes: NurseFullRole[];
	selectedRoles: NurseFullRole[] | undefined;
	roleColor: RoleColorResolver;
}): FacetFilterOption[] {
	const { facetCounts, departmentNurseTypes, selectedRoles, roleColor } = params;
	const selected = selectedRoles ?? [];
	// `departmentNurseTypes` loads async, so a stale role is merged (not normalized) — see buildSourceListOptions.
	return buildSourceListOptions<NurseFullRole>({
		sourceIds: departmentNurseTypes,
		selectedIds: selected,
		sentinelIds: [],
		baselineOf: (id) => facetCounts.baselines.roles.get(id) ?? 0,
		toOption: (fullRole) => {
			const shortRole = inverseRolesMap[fullRole];
			return {
				id: fullRole,
				/**
				 * The short code ("RN"), not the full name ("Registered Nurse (RN)").
				 *
				 * The rail is 264px wide, so every full name truncated to "Registered Nurse (R…" — which
				 * hides the parenthesised code that is the only part staff actually read, and made the
				 * options indistinguishable from each other. `nurseRoleDisplayName` still wins where it
				 * has an override (R-LPN), and a stale/merged role missing from `inverseRolesMap` falls
				 * back to the raw id rather than rendering blank.
				 */
				label: nurseRoleDisplayName[shortRole] ?? shortRole ?? fullRole,
				count: facetCounts.roles.get(fullRole) ?? 0,
				checked: selected.includes(fullRole),
				colorDot: roleColor(fullRole).dot,
			};
		},
	});
}

// ── Jobs (+ explicit "No job assigned" option) ──
export function buildJobOptions(params: { t: TFunction; facetCounts: TeamFacetCounts; availableJobs: Job[]; selectedJobNames: string[] }): FacetFilterOption[] {
	const { t, facetCounts, availableJobs, selectedJobNames } = params;
	// DEFAULT: an enabled job absent from facetCounts has zero currently matching staff; it still
	// renders so this filter mirrors the department's enabled-jobs list.
	const options = availableJobs.map((job) => ({
		id: job.name,
		label: job.displayName,
		count: facetCounts.jobs.get(job.name) ?? 0,
		checked: selectedJobNames.includes(job.name),
	}));
	if (facetCounts.baselines.noJob > 0 || selectedJobNames.includes(NO_JOB_FILTER_ID)) {
		options.push({ id: NO_JOB_FILTER_ID, label: t("team.filter.no_job"), count: facetCounts.noJob, checked: selectedJobNames.includes(NO_JOB_FILTER_ID) });
	}
	return options;
}

// ── Contract Type ──
export function buildContractOptions(params: { facetCounts: TeamFacetCounts; selectedSections: ReadonlySet<string> }): FacetFilterOption[] {
	const { facetCounts, selectedSections } = params;
	return visibleByBaseline(
		TEAM_SECTIONS.map((section) => ({
			id: section.title,
			label: section.title,
			count: facetCounts.contract.get(section.title) ?? 0,
			checked: selectedSections.has(section.title),
		})),
		(id) => facetCounts.baselines.contract.get(id) ?? 0,
	);
}

// ── Union (unions present in the roster + "No union") ──
// The group header always renders; only zero-baseline options hide. With no union
// data in the facility the group still shows its "No union" option (everyone).
/**
 * Last-login buckets, in the ORDER DECLARED — stalest first, never sorted by count.
 *
 * `FacetFilterSection` ranks long groups by count to pick what to show, but this group is short
 * enough to render whole, so the declared order survives. That order is the whole point: the
 * question people bring to this column is "who has gone quiet", asked several times a day, and
 * before this it took three clicks on a date column that then put `Never` at the wrong end.
 *
 * No color coding — the ordering does the work, and a red "Never" would imply a problem the roster
 * can't diagnose (a nurse with no app account looks identical to one who ignores it).
 */
export function buildLastLoginOptions(params: { t: TFunction; facetCounts: TeamFacetCounts; selectedLastLogin: ReadonlySet<string> }): FacetFilterOption[] {
	const { t, facetCounts, selectedLastLogin } = params;
	const labels: Record<string, string> = {
		[LAST_LOGIN_NEVER_ID]: t("team.filter.last_login.never"),
		[LAST_LOGIN_OVER_90_ID]: t("team.filter.last_login.over_90_days"),
		[LAST_LOGIN_31_90_ID]: t("team.filter.last_login.31_90_days"),
		[LAST_LOGIN_8_30_ID]: t("team.filter.last_login.8_30_days"),
		[LAST_LOGIN_PAST_7_ID]: t("team.filter.last_login.past_7_days"),
	};
	return visibleByBaseline(
		LAST_LOGIN_BUCKET_IDS.map((id) => ({
			id,
			label: labels[id],
			count: facetCounts.lastLogin.get(id) ?? 0,
			checked: selectedLastLogin.has(id),
		})),
		(id) => facetCounts.baselines.lastLogin.get(id) ?? 0,
	);
}

export function buildUnionOptions(params: { t: TFunction; facetCounts: TeamFacetCounts; selectedUnions: ReadonlySet<string> }): FacetFilterOption[] {
	const { t, facetCounts, selectedUnions } = params;
	return buildBaselineKeyOptions({
		baselineKeys: facetCounts.baselines.unions.keys(),
		selected: selectedUnions,
		counts: facetCounts.unions,
		// The key is case-folded so one union is one option; the label is the spelling to show.
		// DEFAULT: the `?? key` arm is unreachable — `computeBaselines` writes a label for every key
		// it counts, in the same pass — and exists only because `Map.get` is typed as optional.
		toLabel: (key) => facetCounts.baselines.unionLabels.get(key) ?? key,
		sentinel: { id: NO_UNION_FILTER_ID, label: t("team.filter.no_union"), count: facetCounts.noUnion, baseline: facetCounts.baselines.noUnion },
	});
}

// ── Agency (agencies present in the roster + "None (non-agency)" last) ──
// The group REVEALS rows (agency staff are hidden by the default {None} selection), so
// like a view toggle it is exempt from zero-baseline hiding and the zero-count
// auto-disable: it renders and stays clickable even with zero agency staff.
export function buildAgencyOptions(params: { t: TFunction; facetCounts: TeamFacetCounts; selectedAgencies: ReadonlySet<string> }): FacetFilterOption[] {
	const { t, facetCounts, selectedAgencies } = params;
	return buildBaselineKeyOptions({
		baselineKeys: facetCounts.baselines.agencies.keys(),
		selected: selectedAgencies,
		counts: facetCounts.agencies,
		toLabel: (key) => (key === UNNAMED_AGENCY_FILTER_ID ? t("team.filter.agency.unnamed") : key),
		// Agency REVEALS rows, so every option (incl. "None") stays clickable at count 0 and "None" always shows.
		disabled: false,
		sentinel: { id: NO_AGENCY_FILTER_ID, label: t("team.filter.no_agency"), count: facetCounts.noAgency, baseline: 0, alwaysShow: true },
	});
}

// ── Integrator (sources present in the roster + "None") ──

// ── Email / Phone dimensions (Has / None) ──
// Fixed options (not data-derived like unions/agencies), so both always render; the section
// auto-disables an option whose current count is 0. Separate groups so they AND together —
// "No email" + "No phone" = missing both; "Has email" + "Has phone" = has both.

// ── Auto-approval eligibility (feature-flagged Eligible / Non-eligible dimension, with counts) ──
